import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { IconArrowHead, ResponsiveImage } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ImageCarousel.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showThumbnails: false,
  showFullscreenButton: false,
  slideDuration: 350,
};

const GRID_COLUMNS = 8;
const GRID_ROWS = 4;
const MAIN_IMAGE_COL_SPAN = 4;
const MAIN_IMAGE_ROW_SPAN = 4;
const THUMB_IMAGE_COL_SPAN = 2;
const THUMB_IMAGE_ROW_SPAN = 2;
const MAX_GRID_THUMBNAILS = 4;

/** Thumb slots in the 8×4 grid (one column + half row each in the 4×2 right block). */
const THUMB_GRID_POSITIONS = [
  { column: 5, row: 1 },
  { column: 7, row: 1 },
  { column: 5, row: 3 },
  { column: 7, row: 3 },
];

/**
 * Desktop listing gallery: 8-column grid with a large first image (4×4 cells)
 * and up to four smaller images (2×2 cells each) on the right.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.image>} props.images
 * @param {Array<string>} props.imageVariants
 * @param {string} [props.imageAltId] - Intl message id for image alt text
 * @param {string} [props.sizes] - ResponsiveImage sizes attribute
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Function} [props.onViewAllClick] - Called when "+N more" overlay is clicked
 * @param {string} [props.viewAllLabel] - Accessible label for the "+N more" overlay button
 * @returns {JSX.Element}
 */
export const ImageGrid = props => {
  const intl = useIntl();
  const {
    images = [],
    imageVariants,
    imageAltId = 'ImageCarousel.imageAltText',
    sizes = '(max-width: 1200px) 50vw, 564px',
    className,
    rootClassName,
    onViewAllClick,
    viewAllLabel,
  } = props;

  if (images.length === 0) {
    return (
      <ResponsiveImage
        className={classNames(css.noImage, className)}
        image={null}
        variants={[]}
        alt=""
      />
    );
  }

  const mainImage = images[0];
  const thumbImages = images.slice(1, 1 + MAX_GRID_THUMBNAILS);
  const extraImageCount = Math.max(0, images.length - 1 - MAX_GRID_THUMBNAILS);

  const imageAlt = index =>
    intl.formatMessage({ id: imageAltId }, { index: index + 1, count: images.length });

  return (
    <div
      className={classNames(css.imageGridRoot, rootClassName, className)}
      style={{
        gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
        gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
      }}
    >
      <div
        className={css.imageGridMain}
        style={{
          gridColumn: `1 / span ${MAIN_IMAGE_COL_SPAN}`,
          gridRow: `1 / span ${MAIN_IMAGE_ROW_SPAN}`,
        }}
      >
        <ResponsiveImage
          rootClassName={css.imageGridImage}
          image={mainImage}
          alt={imageAlt(0)}
          variants={imageVariants}
          sizes={sizes}
        />
      </div>

      {thumbImages.map((image, thumbIndex) => {
        const position = THUMB_GRID_POSITIONS[thumbIndex];
        const imageIndex = thumbIndex + 1;
        const isLastThumb = thumbIndex === thumbImages.length - 1;
        const showMoreOverlay = isLastThumb && extraImageCount > 0;

        return (
          <div
            key={image?.id?.uuid || imageIndex}
            className={css.imageGridCell}
            style={{
              gridColumn: `${position.column} / span ${THUMB_IMAGE_COL_SPAN}`,
              gridRow: `${position.row} / span ${THUMB_IMAGE_ROW_SPAN}`,
            }}
          >
            <ResponsiveImage
              rootClassName={css.imageGridImage}
              image={image}
              alt={imageAlt(imageIndex)}
              variants={imageVariants}
              sizes="(max-width: 1200px) 25vw, 280px"
            />
            {showMoreOverlay ? (
              <button
                type="button"
                className={css.imageGridMoreOverlay}
                onClick={onViewAllClick}
                aria-label={viewAllLabel}
              >
                +{extraImageCount}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

/**
 * The ImageCarousel component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @returns {JSX.Element} image carousel component
 */
const ImageCarousel = props => {
  const [currentIndex, setIndex] = useState(0);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants } = props;

  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ImageCarousel.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      image: img,
    };
  });
  const renderItem = item => {
    return (
      <div className={css.imageWrapper}>
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            sizes="(max-width: 767px) 100vw, 80vw"
          />
        </div>
      </div>
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" className={css.arrowHead} />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" className={css.arrowHead} />
        </div>
      </button>
    );
  };

  // If no image is given, rendere empty image.
  if (items.length === 0) {
    const classes = classNames(rootClassName || css.noImage, className);
    return <ResponsiveImage className={classes} image={null} variants={[]} alt="" />;
  }

  // We render index outside of ReactImageGallery.
  // This keeps track of current index aka slide changes happening inside gallery.
  const handleSlide = currentIndex => {
    setIndex(currentIndex);
  };
  const naturalIndex = index => index + 1;

  // Render image index info. E.g. "4/12"
  const imageIndex =
    items.length > 0 ? (
      <span className={css.imageIndex}>
        {naturalIndex(currentIndex)}/{items.length}
      </span>
    ) : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onSlide={handleSlide}
        {...IMAGE_GALLERY_OPTIONS}
      />
      {imageIndex}
    </>
  );
};

export default ImageCarousel;
